

import React from 'react';
import { Layers, Zap, Shield, Globe, BarChart3, Users, Settings, Headphones } from 'lucide-react';

interface FeatureData {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlights: string[];
  color: string;
}

const Features: React.FC = () => {
  const features: FeatureData[] = [
    {
      icon: <Layers className="w-8 h-8" />,
      title: "ChainCloud云超算平台",
      description: "标准化一站式高性能计算平台，按量调度的硬件、开箱即用的软件为您的研发降本增效。不仅能在云上运行，还能加速HPC应用运行。",
      highlights: ["按量调度硬件资源", "开箱即用的软件", "加速HPC应用运行"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "弹性计算",
      description: "根据工作负载自动扩缩容，秒级启动计算实例，支持CPU、GPU、FPGA等多种计算资源类型。",
      highlights: ["自动扩缩容", "秒级启动", "多种资源类型"],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "安全可靠",
      description: "企业级安全保障，数据加密传输存储，完善的权限管理体系，符合国际安全标准。",
      highlights: ["数据加密", "权限管理", "安全合规"],
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "全球部署",
      description: "多地域数据中心，就近接入降低延迟，支持混合云和多云部署架构。",
      highlights: ["多地域部署", "低延迟接入", "混合云支持"],
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "智能监控",
      description: "实时监控计算资源使用情况，智能预警和故障诊断，保障任务稳定运行。",
      highlights: ["实时监控", "智能预警", "故障诊断"],
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "团队协作",
      description: "支持多用户协作，项目管理，资源共享，让团队开发更高效。",
      highlights: ["多用户协作", "项目管理", "资源共享"],
      color: "from-teal-500 to-cyan-500"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "工作流引擎",
      description: "可视化工作流编排，支持复杂计算任务的自动化执行和管理。",
      highlights: ["可视化编排", "自动化执行", "任务管理"],
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: "专业支持",
      description: "7x24小时技术支持，专业的HPC专家团队，为您的项目保驾护航。",
      highlights: ["24小时支持", "专家团队", "项目保障"],
      color: "from-pink-500 to-rose-500"
    }
  ];

  return (
    <section className="py-12 lg:py-16 xl:py-20 2xl:py-24 portrait:py-8 bg-white">
      <div className="container-responsive">
        {/* Header */}
        <div className="text-center mb-8 lg:mb-12 xl:mb-16 portrait:mb-6">
          <h2 className="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl portrait:text-xl font-bold text-gray-900 mb-3 lg:mb-4 xl:mb-6 portrait:mb-2">
            强大功能，助力创新
          </h2>
          <p className="text-sm lg:text-base xl:text-lg 2xl:text-xl portrait:text-sm text-gray-600 max-w-3xl mx-auto">
            ChainCloud提供全方位的云超算解决方案，满足各种计算密集型应用需求
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6 xl:gap-8 portrait:gap-3">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg p-4 lg:p-6 xl:p-8 portrait:p-3 border border-gray-100 hover:border-transparent shadow-sm hover:shadow-xl portrait:shadow-none portrait:hover:shadow-lg hover:-translate-y-2 portrait:hover:-translate-y-1 transition-all duration-500"
            >
              {/* Background Gradient on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`w-12 h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 2xl:w-18 2xl:h-18 portrait:w-10 portrait:h-10 bg-gradient-to-br ${feature.color} rounded-lg lg:rounded-xl portrait:rounded-md flex items-center justify-center text-white mb-4 lg:mb-6 xl:mb-8 portrait:mb-3 group-hover:scale-110 transition-transform duration-300`}>
                {React.cloneElement(feature.icon as React.ReactElement, { 
                  className: "w-5 h-5 lg:w-6 lg:h-6 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 portrait:w-4 portrait:h-4" 
                })}
              </div>

              {/* Title */}
              <h3 className="text-base lg:text-lg xl:text-xl 2xl:text-2xl portrait:text-sm font-bold text-gray-900 mb-2 lg:mb-3 xl:mb-4 portrait:mb-2 group-hover:text-gray-800 transition-colors duration-300">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-xs lg:text-sm xl:text-base portrait:text-xs text-gray-600 leading-relaxed mb-3 lg:mb-4 xl:mb-6 portrait:mb-2">
                {feature.description}
              </p>

              {/* Highlights */}
              <div className="space-y-1.5 lg:space-y-2 xl:space-y-3 portrait:space-y-1">
                {feature.highlights.map((highlight, highlightIndex) => (
                  <div key={highlightIndex} className="flex items-center text-xs lg:text-sm xl:text-base portrait:text-xs text-gray-500">
                    <div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 portrait:w-1 portrait:h-1 bg-gradient-to-r ${feature.color} rounded-full mr-2 lg:mr-3 portrait:mr-1.5`} />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>

              {/* Decorative Elements */}
              <div className={`absolute top-3 lg:top-4 xl:top-6 portrait:top-2 right-3 lg:right-4 xl:right-6 portrait:right-2 w-1.5 h-1.5 lg:w-2 lg:h-2 xl:w-3 xl:h-3 portrait:w-1 portrait:h-1 bg-gradient-to-br ${feature.color} rounded-full opacity-30 group-hover:opacity-70 transition-opacity duration-300`} />
              <div className={`absolute bottom-3 lg:bottom-4 xl:bottom-6 portrait:bottom-2 right-3 lg:right-4 xl:right-6 portrait:right-2 w-1 h-1 lg:w-1.5 lg:h-1.5 xl:w-2 xl:h-2 portrait:w-0.5 portrait:h-0.5 bg-gradient-to-br ${feature.color} rounded-full opacity-20 group-hover:opacity-50 transition-opacity duration-300`} />
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="mt-8 lg:mt-12 xl:mt-16 portrait:mt-6 text-center">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg p-4 lg:p-6 xl:p-8 portrait:p-3">
            <h3 className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl portrait:text-base font-bold text-gray-900 mb-2 lg:mb-3 xl:mb-4 portrait:mb-2">
              还有更多强大功能等你探索
            </h3>
            <p className="text-sm lg:text-base xl:text-lg portrait:text-xs text-gray-600 mb-3 lg:mb-4 xl:mb-6 portrait:mb-2 max-w-2xl mx-auto">
              从数据预处理到结果分析，从单机计算到分布式集群，ChainCloud为您提供完整的计算解决方案
            </p>
            <button className="px-4 lg:px-6 xl:px-8 portrait:px-3 py-2 lg:py-3 xl:py-4 portrait:py-1.5 text-sm lg:text-base xl:text-lg portrait:text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg xl:rounded-xl portrait:rounded-md hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              了解更多功能
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 